/**
 * Line-art illustrations for empty states.
 * Monochrome, stroke-only, use currentColor so they adapt to parent color.
 * Consistent style: 1.5px stroke, rounded joins, subtle dotted ground lines.
 */
import React from "react";

const base = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

// Main workbench empty state — stylised butterfly valve + pipes (200px)
export function EmptyWorkbench({ className = "" }) {
  return (
    <svg
      viewBox="0 0 220 140"
      width="200"
      height="128"
      className={className}
      aria-hidden="true"
    >
      <g {...base}>
        <rect x="10" y="58" width="60" height="28" rx="2" />
        <rect x="64" y="50" width="10" height="44" rx="1.5" />
        <circle cx="110" cy="72" r="30" />
        <ellipse cx="110" cy="72" rx="26" ry="7" transform="rotate(-18 110 72)" />
        <path d="M110 42v-16" />
        <path d="M102 28h16" />
        <rect x="146" y="50" width="10" height="44" rx="1.5" />
        <rect x="156" y="58" width="54" height="28" rx="2" />
        <circle cx="69" cy="56" r="1.2" />
        <circle cx="69" cy="88" r="1.2" />
        <circle cx="151" cy="56" r="1.2" />
        <circle cx="151" cy="88" r="1.2" />
        <path d="M20 120h180" opacity="0.3" strokeDasharray="2 4" />
      </g>
    </svg>
  );
}

// Clock + dotted timeline (80px)
export function EmptyHistory({ className = "" }) {
  return (
    <svg
      viewBox="0 0 80 80"
      width="80"
      height="80"
      className={className}
      aria-hidden="true"
    >
      <g {...base}>
        <circle cx="28" cy="40" r="16" />
        <path d="M28 30v10l7 4" />
        <circle cx="56" cy="24" r="1.8" />
        <circle cx="56" cy="40" r="1.8" />
        <circle cx="56" cy="56" r="1.8" />
        <path d="M56 28v8M56 44v8" strokeDasharray="2 3" />
      </g>
    </svg>
  );
}

// Stacked paper sheet with lines (64px)
export function EmptyStorico({ className = "" }) {
  return (
    <svg
      viewBox="0 0 64 64"
      width="64"
      height="64"
      className={className}
      aria-hidden="true"
    >
      <g {...base}>
        <rect x="14" y="12" width="32" height="40" rx="3" />
        <path d="M20 22h20M20 28h20M20 34h14M20 40h18" opacity="0.7" />
        <path d="M46 12v8h-8" fill="none" />
      </g>
    </svg>
  );
}

// Offers list — stacked sheets with price tag (140px)
export function EmptyOffers({ className = "" }) {
  return (
    <svg
      viewBox="0 0 160 120"
      width="140"
      height="106"
      className={className}
      aria-hidden="true"
    >
      <g {...base}>
        {/* Back sheet */}
        <rect x="30" y="20" width="70" height="86" rx="3" opacity="0.4" />
        {/* Front sheet */}
        <rect x="42" y="12" width="70" height="86" rx="3" />
        <path d="M50 26h54M50 34h54M50 42h40M50 52h54M50 60h48M50 68h32" opacity="0.7" />
        {/* Price tag corner */}
        <path d="M112 12l16 16-10 10-16-16z" />
        <circle cx="120" cy="20" r="2.2" />
        {/* Euro mark */}
        <path d="M122 30c-2-2-6-2-8 0-2 2-2 6 0 8 2 2 6 2 8 0 M118 32h6 M118 36h6" opacity="0.9" strokeWidth="1.2" />
        {/* Ground dots */}
        <path d="M20 114h120" opacity="0.25" strokeDasharray="2 4" />
      </g>
    </svg>
  );
}

// Log / chart — bar chart with trend line (140px)
export function EmptyLog({ className = "" }) {
  return (
    <svg
      viewBox="0 0 160 120"
      width="140"
      height="106"
      className={className}
      aria-hidden="true"
    >
      <g {...base}>
        {/* Axes */}
        <path d="M20 16v86h120" />
        {/* Bars */}
        <rect x="36" y="64" width="14" height="38" rx="1.2" />
        <rect x="60" y="48" width="14" height="54" rx="1.2" />
        <rect x="84" y="74" width="14" height="28" rx="1.2" />
        <rect x="108" y="38" width="14" height="64" rx="1.2" />
        {/* Trend line */}
        <path
          d="M28 80l22-14 24 6 24-20 26-8"
          strokeDasharray="3 3"
          opacity="0.6"
        />
        {/* Peak marker */}
        <circle cx="120" cy="30" r="2.2" />
      </g>
    </svg>
  );
}

// Users — three silhouette circles + shoulders (140px)
export function EmptyUsers({ className = "" }) {
  return (
    <svg
      viewBox="0 0 160 120"
      width="140"
      height="106"
      className={className}
      aria-hidden="true"
    >
      <g {...base}>
        {/* Center user (larger) */}
        <circle cx="80" cy="44" r="14" />
        <path d="M56 96c0-14 10-24 24-24s24 10 24 24" />
        {/* Left user */}
        <circle cx="36" cy="52" r="10" opacity="0.6" />
        <path d="M20 96c0-10 7-18 16-18" opacity="0.6" />
        {/* Right user */}
        <circle cx="124" cy="52" r="10" opacity="0.6" />
        <path d="M140 96c0-10-7-18-16-18" opacity="0.6" />
        {/* Ground */}
        <path d="M10 108h140" opacity="0.25" strokeDasharray="2 4" />
      </g>
    </svg>
  );
}

// Trash bin — open lid, empty interior (140px)
export function EmptyTrash({ className = "" }) {
  return (
    <svg
      viewBox="0 0 160 120"
      width="140"
      height="106"
      className={className}
      aria-hidden="true"
    >
      <g {...base}>
        {/* Lid (tilted) */}
        <path d="M50 26l64-8" />
        <rect x="52" y="20" width="60" height="6" rx="2" transform="rotate(-6 82 23)" />
        {/* Handle on lid */}
        <path d="M74 16l14-2" transform="rotate(-6 82 15)" />
        {/* Bin body (trapezoid) */}
        <path d="M42 40l8 64h60l8-64z" />
        <path d="M42 40h84" />
        {/* Vertical stripes */}
        <path d="M62 50v44M80 50v44M98 50v44" opacity="0.5" />
        {/* Soft sparkle (empty) */}
        <path d="M130 58l2 4 4 2-4 2-2 4-2-4-4-2 4-2z" opacity="0.7" />
        <path d="M28 78l1.4 2.8 2.8 1.4-2.8 1.4-1.4 2.8-1.4-2.8-2.8-1.4 2.8-1.4z" opacity="0.6" />
      </g>
    </svg>
  );
}
